import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Check, 
  Crown, 
  Zap, 
  Star, 
  Users, 
  Bot, 
  TrendingUp, 
  Shield,
  Headphones,
  Camera
} from "lucide-react";
import { AnimatedBackground } from "./AnimatedBackground";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useAnalytics } from "@/hooks/useAnalytics";

interface SubscriptionTier {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  billing_cycle: string;
  features: string[];
  max_tips_per_day: number;
  early_access: boolean;
  ai_bundle_access: boolean;
  priority_support: boolean;
  is_active: boolean;
}

interface SubscriptionPlansProps {
  onPurchase?: (tierId: string) => void;
}

export function SubscriptionPlans({ onPurchase }: SubscriptionPlansProps) {
  const [plans, setPlans] = useState<SubscriptionTier[]>([]);
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();
  const { trackSubscribe, trackViewItem } = useAnalytics();

  useEffect(() => {
    fetchPlans();
    if (user) fetchCurrentSubscription();
  }, [user]);

  const fetchPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_tiers')
        .select('*')
        .eq('is_active', true)
        .order('price', { ascending: true });

      if (error) throw error;
      setPlans(data || []);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      toast({
        title: "Error",
        description: "Failed to load subscription plans",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('tier_id')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      setCurrentPlan(data?.tier_id || null);
    } catch (error) {
      console.error('Error fetching current subscription:', error);
    }
  };

  const handlePurchase = (tierId: string) => {
    const plan = plans.find(p => p.id === tierId);
    if (plan) {
      // Track subscription event
      trackSubscribe(tierId, plan.name, plan.price, plan.billing_cycle);
      
      // Track view item for GA4
      trackViewItem({
        id: tierId,
        name: plan.name,
        category: 'Subscription',
        price: plan.price
      });
    }
    
    if (onPurchase) {
      onPurchase(tierId);
    }
  };

  const getPlanIcon = (planName: string) => {
    switch (planName.toLowerCase()) {
      case 'starter': return <Star className="w-6 h-6" />;
      case 'pro': return <Zap className="w-6 h-6" />;
      case 'elite': return <Crown className="w-6 h-6" />;
      default: return <Star className="w-6 h-6" />;
    }
  };

  const getPlanColor = (planName: string) => {
    switch (planName.toLowerCase()) {
      case 'starter': return 'text-blue-500';
      case 'pro': return 'text-purple-500';
      case 'elite': return 'text-gold-500';
      default: return 'text-primary';
    }
  };

  const getFeatureIcon = (feature: string) => {
    if (feature.toLowerCase().includes('ai')) return <Bot className="w-4 h-4" />;
    if (feature.toLowerCase().includes('support')) return <Headphones className="w-4 h-4" />;
    if (feature.toLowerCase().includes('analytics')) return <TrendingUp className="w-4 h-4" />;
    if (feature.toLowerCase().includes('sharing')) return <Camera className="w-4 h-4" />;
    if (feature.toLowerCase().includes('community')) return <Users className="w-4 h-4" />;
    if (feature.toLowerCase().includes('priority')) return <Shield className="w-4 h-4" />;
    return <Check className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map(i => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-8 bg-muted rounded w-3/4" />
              <div className="h-6 bg-muted rounded w-1/2" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="h-12 bg-muted rounded" />
                <div className="h-32 bg-muted rounded" />
                <div className="h-10 bg-muted rounded" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <AnimatedBackground variant="minimal">
      <div className="space-y-8">
        <div className="text-center space-y-4">
          <h2 className="text-3xl font-bold">Choose Your Plan</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Unlock premium features, AI-powered insights, and exclusive content with our flexible subscription plans.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan, index) => {
            const isCurrentPlan = currentPlan === plan.id;
            const isPopular = index === 1; // Middle plan is popular
            const isPremium = plan.name.toLowerCase() === 'elite';
            
            return (
              <Card 
                key={plan.id} 
                className={`relative overflow-hidden hover:shadow-elegant transition-all duration-300 hover-scale group ${
                  isPremium ? 'border-gold-500/50 bg-gradient-to-br from-gold-500/5 to-background' : ''
                } ${isCurrentPlan ? 'ring-2 ring-primary' : ''}`}
              >
                {isPopular && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <Badge className="bg-primary text-primary-foreground px-3 py-1">
                      Most Popular
                    </Badge>
                  </div>
                )}

                {isPremium && (
                  <div className="absolute inset-0 bg-gradient-premium opacity-5" />
                )}

                <CardHeader className="text-center space-y-4">
                  <div className={`mx-auto ${getPlanColor(plan.name)}`}>
                    {getPlanIcon(plan.name)}
                  </div>
                  
                  <div>
                    <CardTitle className="text-2xl flex items-center justify-center gap-2">
                      {plan.name}
                      {isPremium && <Crown className="w-5 h-5 text-gold-500" />}
                    </CardTitle>
                    <p className="text-muted-foreground mt-2">{plan.description}</p>
                  </div>

                  <div className="space-y-1">
                    <div className="text-4xl font-bold">
                      ${plan.price}
                      <span className="text-lg font-normal text-muted-foreground">
                        /{plan.billing_cycle === 'monthly' ? 'mo' : 'yr'}
                      </span>
                    </div>
                    {plan.billing_cycle === 'yearly' && (
                      <Badge variant="outline" className="text-xs">
                        Save 20%
                      </Badge>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span>Daily tip limit:</span>
                      <span className="font-medium">
                        {plan.max_tips_per_day === -1 ? 'Unlimited' : plan.max_tips_per_day}
                      </span>
                    </div>
                    
                    <Separator />
                    
                    <div className="space-y-3">
                      {plan.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-start gap-3">
                          <div className="text-primary mt-0.5">
                            {getFeatureIcon(feature)}
                          </div>
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                      
                      {plan.early_access && (
                        <div className="flex items-start gap-3">
                          <Zap className="w-4 h-4 text-primary mt-0.5" />
                          <span className="text-sm">Early access to new features</span>
                        </div>
                      )}
                      
                      {plan.ai_bundle_access && (
                        <div className="flex items-start gap-3">
                          <Bot className="w-4 h-4 text-primary mt-0.5" />
                          <span className="text-sm">AI tip bundles included</span>
                        </div>
                      )}
                      
                      {plan.priority_support && (
                        <div className="flex items-start gap-3">
                          <Headphones className="w-4 h-4 text-primary mt-0.5" />
                          <span className="text-sm">Priority customer support</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <Button
                    variant={isPremium ? 'premium' : isPopular ? 'default' : 'outline'}
                    className="w-full font-semibold"
                    size="lg"
                    onClick={() => handlePurchase(plan.id)}
                    disabled={isCurrentPlan}
                  >
                    {isCurrentPlan ? (
                      "Current Plan"
                    ) : plan.price === 0 ? (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Get Started Free
                      </>
                    ) : isPremium ? (
                      <>
                        <Crown className="w-4 h-4 mr-2" />
                        Subscribe Now - ${plan.price}/mo
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Subscribe Now - ${plan.price}/mo
                      </>
                    )}
                  </Button>

                  {isCurrentPlan && (
                    <div className="text-center">
                      <Badge variant="outline" className="text-xs">
                        <Check className="w-3 h-3 mr-1" />
                        Active Subscription
                      </Badge>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center space-y-4">
          <p className="text-sm text-muted-foreground">
            All plans include access to our community, basic analytics, and free tips.
          </p>
          <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Shield className="w-3 h-3" />
              Secure payments
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-3 h-3" />
              Cancel anytime
            </span>
            <span className="flex items-center gap-1">
              <Headphones className="w-3 h-3" />
              24/7 support
            </span>
          </div>
        </div>
      </div>
    </AnimatedBackground>
  );
}